<template>
  <div class="card p-4 shadow-sm">
    <!-- Header con estadísticas -->
    <div class="row mb-4">
      <div class="col-md-8">
        <h3 class="m-0">Venta de Tienda</h3>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end gap-3">
          <div class="text-center">
            <small class="text-muted d-block">Productos</small>
            <strong class="text-primary">{{ productos.length }}</strong>
          </div>
          <div class="text-center">
            <small class="text-muted d-block">En Carrito</small>
            <strong class="text-success">{{ carrito.length }}</strong>
          </div>
        </div>
      </div>
    </div>

    <!-- Buscador mejorado -->
    <div class="mb-3">
      <div class="input-group">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input type="text" class="form-control" placeholder="Buscar producto por nombre..." v-model="busqueda" @input="debouncedBuscarProductos" />
        <button v-if="busqueda" class="btn btn-outline-secondary" @click="limpiarBusqueda">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Productos encontrados - Mejorado -->
    <div v-if="productosFiltradosResultado.length" class="mb-4">
      <div class="alert alert-info py-2">
        <i class="bi bi-info-circle me-2"></i>
        <strong>{{ productosFiltradosResultado.length }}</strong> producto(s) encontrado(s)
      </div>
      <div class="row g-2">
        <div class="col-md-3" v-for="prod in productosFiltradosResultado" :key="prod.IdProductoTienda">
          <div class="border rounded p-2 h-100 d-flex flex-column" :class="{'border-warning': prod.Cantidad < 5, 'border-danger': prod.Cantidad === 0}">
            <div class="mb-2">
              <strong class="d-block">{{ prod.Nombre }}</strong>
              <span class="text-primary">Q{{ Number(prod.PrecioVenta).toFixed(2) }}</span>
            </div>
            <div class="mt-auto d-flex justify-content-between align-items-center">
              <small :class="{'text-danger': prod.Cantidad === 0, 'text-warning': prod.Cantidad < 5, 'text-success': prod.Cantidad >= 5}">
                <i class="bi bi-box-seam me-1"></i>{{ prod.Cantidad }} disponible(s)
              </small>
              <button
                class="btn btn-sm btn-outline-primary"
                @click="agregarProducto(prod)"
                :disabled="prod.Cantidad === 0 || estaEnCarrito(prod.IdProductoTienda)"
              >
                {{ estaEnCarrito(prod.IdProductoTienda) ? 'Agregado' : 'Agregar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Carrito -->
    <div class="table-responsive mb-4">
      <table class="table table-bordered align-middle text-center">
        <thead class="table-light">
          <tr>
            <th>Producto</th>
            <th>Precio (Q)</th>
            <th>Cantidad</th>
            <th>Total (Q)</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in carrito" :key="item.IdProductoTienda">
            <td>{{ item.Nombre }}</td>
            <td>Q{{ Number(item.PrecioVenta).toFixed(2) }}</td>
            <td>
              <input type="number" v-model.number="item.cantidad" class="form-control form-control-sm" min="1" />
            </td>
            <td>Q{{ (item.cantidad * item.PrecioVenta).toFixed(2) }}</td>
            <td>
              <button class="btn btn-sm btn-danger" @click="eliminarProducto(index)">Eliminar</button>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th colspan="3" class="text-end">Total Venta:</th>
            <th colspan="2">Q{{ totalVenta.toFixed(2) }}</th>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Métodos de pago -->
    <div class="mb-4">
      <h5>Métodos de Pago</h5>
      <div class="row g-3 mb-2" v-for="(pago, index) in pagos" :key="index">
        <div class="col-md-3">
          <select v-model="pago.IdMetodoPago" class="form-select">
            <option disabled value="">Seleccione método</option>
            <option v-for="m in metodosPago" :key="m.IdMetodoPago" :value="m.IdMetodoPago">{{ m.Metodo }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <input type="number" v-model.number="pago.Cantidad" min="0.01" step="0.01" class="form-control" placeholder="Monto Q" :disabled="!pago.IdMetodoPago" />
        </div>
        <div class="col-md-3" v-if="pago.IdMetodoPago == 2">
          <select v-model="pago.IdCuentaBancaria" class="form-select">
            <option disabled value="">Seleccione cuenta bancaria</option>
            <option v-for="cuenta in cuentasBancarias" :key="cuenta.IdCuentaBancaria" :value="cuenta.IdCuentaBancaria">
              {{ cuenta.Banco }} - {{ cuenta.NumeroCuenta }}
            </option>
          </select>
        </div>
        <div class="col-md-3" v-if="pago.IdMetodoPago == 2 || pago.IdMetodoPago == 3 || pago.IdMetodoPago == 4">
          <input type="text" v-model="pago.NumeroReferencia" class="form-control" placeholder="Referencia" />
        </div>
        <div class="col-md-1 d-flex align-items-center">
          <button class="btn btn-danger btn-sm" @click="removerPago(index)" :disabled="pagos.length === 1">
            X
          </button>
        </div>
      </div>
      <div class="d-flex justify-content-between align-items-center mt-3">
        <button class="btn btn-outline-success btn-sm" @click="agregarPago">+ Agregar Método de Pago</button>
        <div>
          <span :class="{'text-success': totalPagado === totalVenta, 'text-danger': totalPagado !== totalVenta}">
            <strong>Total Pagado: Q{{ totalPagado.toFixed(2) }}</strong>
            <span v-if="totalPagado !== totalVenta" class="ms-2">(Faltan: Q{{ (totalVenta - totalPagado).toFixed(2) }})</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Botón -->
    <div class="text-end">
      <button class="btn btn-primary" :disabled="!puedeFinalizar" @click="finalizarVenta">
        Registrar Venta
      </button>
    </div>

    <!-- Toast -->
    <div v-if="mostrarToast" class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1055;">
      <div class="toast show text-white" :class="toastColor">
        <div class="d-flex">
          <div class="toast-body">
            <i :class="iconoToast" class="me-2"></i> {{ alerta.mensaje }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
        </div>
      </div>
    </div>

    <!-- Tabla de Inventario Completo -->
    <div class="mt-5 pt-4 border-top">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="m-0">
          <i class="bi bi-box-seam me-2"></i>Inventario Completo
        </h4>
        <div class="btn-group" role="group">
          <input type="radio" class="btn-check" name="filtroStock" id="todos" value="todos" v-model="filtroStock" autocomplete="off" checked>
          <label class="btn btn-outline-secondary btn-sm" for="todos">Todos</label>

          <input type="radio" class="btn-check" name="filtroStock" id="disponible" value="disponible" v-model="filtroStock" autocomplete="off">
          <label class="btn btn-outline-success btn-sm" for="disponible">Disponibles</label>

          <input type="radio" class="btn-check" name="filtroStock" id="bajo" value="bajo" v-model="filtroStock" autocomplete="off">
          <label class="btn btn-outline-warning btn-sm" for="bajo">Stock Bajo</label>

          <input type="radio" class="btn-check" name="filtroStock" id="agotado" value="agotado" v-model="filtroStock" autocomplete="off">
          <label class="btn btn-outline-danger btn-sm" for="agotado">Agotados</label>
        </div>
      </div>

      <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
        <table class="table table-hover table-sm align-middle">
          <thead class="table-light sticky-top">
            <tr>
              <th style="width: 50px;">#</th>
              <th>Producto</th>
              <th class="text-center" style="width: 120px;">Stock</th>
              <th class="text-end" style="width: 120px;">Precio (Q)</th>
              <th class="text-center" style="width: 120px;">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(prod, index) in productosFiltrados" :key="prod.IdProductoTienda">
              <td>{{ index + 1 }}</td>
              <td>
                <strong>{{ prod.Nombre }}</strong>
                <small class="text-muted d-block" v-if="prod.Descripcion">{{ prod.Descripcion }}</small>
              </td>
              <td class="text-center">
                <span
                  class="badge"
                  :class="{
                    'bg-danger': prod.Cantidad === 0,
                    'bg-warning text-dark': prod.Cantidad > 0 && prod.Cantidad < 5,
                    'bg-success': prod.Cantidad >= 5
                  }"
                >
                  <i class="bi bi-box-seam me-1"></i>{{ prod.Cantidad }}
                </span>
              </td>
              <td class="text-end">
                <strong class="text-primary">Q{{ Number(prod.PrecioVenta).toFixed(2) }}</strong>
              </td>
              <td class="text-center">
                <button
                  class="btn btn-sm"
                  :class="estaEnCarrito(prod.IdProductoTienda) ? 'btn-secondary' : 'btn-outline-primary'"
                  @click="agregarProducto(prod)"
                  :disabled="prod.Cantidad === 0 || estaEnCarrito(prod.IdProductoTienda)"
                >
                  <i class="bi" :class="estaEnCarrito(prod.IdProductoTienda) ? 'bi-check-lg' : 'bi-cart-plus'"></i>
                  {{ estaEnCarrito(prod.IdProductoTienda) ? 'En carrito' : 'Agregar' }}
                </button>
              </td>
            </tr>
            <tr v-if="productosFiltrados.length === 0">
              <td colspan="5" class="text-center text-muted py-4">
                <i class="bi bi-inbox fs-1 d-block mb-2"></i>
                No hay productos {{ filtroStock !== 'todos' ? `con filtro "${filtroStock}"` : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-2 text-muted small">
        <i class="bi bi-info-circle me-1"></i>
        Mostrando {{ productosFiltrados.length }} de {{ productos.length }} productos
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '@/config/config.js';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import { imprimirReciboTienda } from '@/utils/impresion';

export default {
  name: 'VentaTienda',
  data() {
    return {
      busqueda: '',
      productos: [],
      carrito: [],
      metodosPago: [],
      pagos: [{ IdMetodoPago: '', Cantidad: '', NumeroReferencia: '', IdCuentaBancaria: '' }],
      productosFiltradosResultado: [],
      debouncedBuscarProductos: null,
      alerta: { mensaje: '', tipo: 'success' },
      mostrarToast: false,
      cuentasBancarias: [],
      filtroStock: 'todos', // Para filtrar productos por stock
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    totalVenta() {
      return this.carrito.reduce((total, item) => total + item.PrecioVenta * item.cantidad, 0);
    },
    totalPagado() {
      return this.pagos.reduce((total, p) => total + (Number(p.Cantidad) || 0), 0);
    },
    puedeFinalizar() {
      if (!this.carrito.length) return false;
      if (this.totalPagado !== this.totalVenta) return false;
      if (!this.pagos.some(p => p.IdMetodoPago && p.Cantidad > 0)) return false;
      const pagosActivos = this.pagos.filter(p => p.IdMetodoPago && p.Cantidad > 0);
      if (pagosActivos.some(p => this.requiereReferencia(p.IdMetodoPago) && !String(p.NumeroReferencia).trim())) return false;
      return true;
    },
    toastColor() {
      return {
        success: 'bg-success',
        danger: 'bg-danger',
        warning: 'bg-warning text-dark',
        info: 'bg-info text-dark'
      }[this.alerta.tipo] || 'bg-primary';
    },
    iconoToast() {
      return {
        success: 'bi bi-check-circle-fill',
        danger: 'bi bi-x-circle-fill',
        warning: 'bi bi-exclamation-triangle-fill',
        info: 'bi bi-info-circle-fill'
      }[this.alerta.tipo] || 'bi bi-info-circle-fill';
    },
    productosFiltrados() {
      let filtered = [...this.productos];

      switch(this.filtroStock) {
        case 'disponible':
          filtered = filtered.filter(p => p.Cantidad > 0);
          break;
        case 'bajo':
          filtered = filtered.filter(p => p.Cantidad > 0 && p.Cantidad < 5);
          break;
        case 'agotado':
          filtered = filtered.filter(p => p.Cantidad === 0);
          break;
      }

      return filtered;
    }
  },
  methods: {
    async obtenerProductos() {
      const res = await axios.get(`${API_URL}/inventario-tienda`);
      this.productos = res.data;
    },
    async buscarProductos() {
      if (!this.busqueda.trim()) {
        this.productosFiltradosResultado = [];
        return;
      }
      this.productosFiltradosResultado = this.productos.filter(p =>
        p.Nombre.toLowerCase().includes(this.busqueda.toLowerCase())
      );
    },
    limpiarBusqueda() {
      this.busqueda = '';
      this.productosFiltradosResultado = [];
    },
    estaEnCarrito(idProducto) {
      return this.carrito.some(p => p.IdProductoTienda === idProducto);
    },
    agregarProducto(prod) {
      if (prod.Cantidad === 0) {
        this.mostrarToastMensaje('Producto sin stock disponible', 'warning');
        return;
      }
      const existe = this.carrito.find(p => p.IdProductoTienda === prod.IdProductoTienda);
      if (existe) {
        this.mostrarToastMensaje('El producto ya está en el carrito', 'info');
        return;
      }
      this.carrito.push({ ...prod, cantidad: 1 });
      this.mostrarToastMensaje(`${prod.Nombre} agregado al carrito`, 'success');
    },
    eliminarProducto(index) {
      this.carrito.splice(index, 1);
    },
    async obtenerMetodosPago() {
      const res = await axios.get(`${API_URL}/metodos-pago/no-facturados`);
      this.metodosPago = Array.isArray(res.data) ? res.data : [];
    },
    async obtenerCuentasBancarias() {
      try {
        const res = await axios.get(`${API_URL}/cuentas-bancarias`);
        this.cuentasBancarias = res.data;
      } catch (error) {
        console.error('Error al obtener cuentas bancarias:', error);
      }
    },
    requiereReferencia(idMetodo) {
      return [2, 3, 4].includes(Number(idMetodo));
    },
    agregarPago() {
      this.pagos.push({ IdMetodoPago: '', Cantidad: '', NumeroReferencia: '', IdCuentaBancaria: '' });
    },
    removerPago(index) {
      if (this.pagos.length > 1) {
        this.pagos.splice(index, 1);
      }
    },
    mostrarToastMensaje(mensaje, tipo = 'success') {
      this.alerta.mensaje = mensaje;
      this.alerta.tipo = tipo;
      this.mostrarToast = true;
      setTimeout(() => {
        this.mostrarToast = false;
      }, 3000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    },
    async finalizarVenta() {
      try {
        const payload = {
          Total: this.totalVenta,
          IdEmpleado: this.getUser?.IdEmpleado || null,
          detalles: this.carrito.map(p => ({
            IdProductoTienda: p.IdProductoTienda,
            Cantidad: p.cantidad
          })),
          pagos: this.pagos.filter(p => p.IdMetodoPago && p.Cantidad > 0).map(p => ({
            Monto: Number(p.Cantidad),
            IdMetodoPago: p.IdMetodoPago,
            IdCuentaBancaria: p.IdCuentaBancaria || null,
            NumeroReferencia: this.requiereReferencia(p.IdMetodoPago) ? p.NumeroReferencia : null,
          }))
        };

        await axios.post(`${API_URL}/ventas-tienda`, payload);
        this.mostrarToastMensaje('Venta registrada correctamente');

        // Imprimir recibo automáticamente después de 500ms
        setTimeout(() => {
          const pagosConMetodo = this.pagos
            .filter(p => p.IdMetodoPago && p.Cantidad > 0)
            .map(p => {
              const metodo = this.metodosPago.find(m => m.IdMetodoPago === p.IdMetodoPago);
              return {
                ...p,
                Metodo: metodo?.Metodo || 'N/A',
                Monto: p.Cantidad
              };
            });
          imprimirReciboTienda(
            { Total: this.totalVenta },
            this.carrito,
            pagosConMetodo
          );
        }, 500);

        // Limpiar formulario
        this.carrito = [];
        this.pagos = [{ IdMetodoPago: '', Cantidad: '', NumeroReferencia: '', IdCuentaBancaria: '' }];
        this.busqueda = '';
        this.productosFiltradosResultado = [];

        // Recargar inventario para actualizar cantidades
        await this.obtenerProductos();

      } catch (error) {
        console.error(error);
        const mensaje = error.response?.data?.message || 'Error al registrar la venta.';
        this.mostrarToastMensaje(mensaje, 'danger');
      }
    }
  },
  created() {
    this.debouncedBuscarProductos = debounce(this.buscarProductos, 400);
  },
  mounted() {
    this.obtenerProductos();
    this.obtenerMetodosPago();
    this.obtenerCuentasBancarias();
  }
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
